import type { JunctionDto } from "@/api/generated/models/junctionDto";
import type { JunctionMovementDto } from "@/api/generated/models/junctionMovementDto";
import type { MovementVehicleCountDto } from "@/api/generated/models/movementVehicleCountDto";
import { TrajectoryPointDtoVehicleType } from "@/api/generated/models/trajectoryPointDtoVehicleType";
import { buildMovementColorMap } from "@/utils/colourUtils";

export interface JunctionMovementCounts {
  junction: JunctionDto;
  counts: MovementVehicleCountDto[];
  movements: JunctionMovementDto[];
}

export interface JunctionMovementRowVehicleCount {
  vehicleType: TrajectoryPointDtoVehicleType;
  count: number;
}

export interface JunctionMovementRow {
  movement: JunctionMovementDto;
  total: number;
  color: string;
  vehicleCounts: JunctionMovementRowVehicleCount[];
}

export const buildJunctionMovementRows = (
  counts: MovementVehicleCountDto[],
  movements: JunctionMovementDto[],
): JunctionMovementRow[] => {
  const movementsById = new Map(movements.map((m) => [m.id, m]));
  const byId = new Map<number, Omit<JunctionMovementRow, "color">>();

  for (const entry of counts) {
    const vehicleCount: JunctionMovementRowVehicleCount = {
      vehicleType: entry.vehicleType as TrajectoryPointDtoVehicleType,
      count: entry.count,
    };

    const existing = byId.get(entry.movementId);
    if (existing) {
      existing.total += entry.count;
      existing.vehicleCounts.push(vehicleCount);
      continue;
    }

    byId.set(entry.movementId, {
      movement: movementsById.get(entry.movementId)!,
      total: entry.count,
      vehicleCounts: [vehicleCount],
    });
  }

  const colorByMovementId = buildMovementColorMap(byId.keys());

  return [...byId.values()]
    .sort((a, b) => a.movement.id - b.movement.id)
    .map((row) => ({
      ...row,
      color: colorByMovementId.get(row.movement.id)!,
    }));
};
